/**
 * @zendir/ui - Chart Synchronization Utilities
 *
 * Enables crosshair, tooltip, and zoom synchronization across multiple charts.
 * Useful for dashboard views with related time-series data.
 */

import { useEffect, useRef } from "react";
import type { ECharts } from "echarts";
import type { AstroChartHandle } from "./AstroChart";

// =============================================================================
// Sync Group Manager
// =============================================================================

interface SyncGroup {
  id: string;
  charts: Set<ECharts>;
  options: SyncGroupOptions;
}

interface SyncGroupOptions {
  /** Sync tooltip position */
  tooltip?: boolean;
  /** Sync crosshair */
  crosshair?: boolean;
  /** Sync zoom/pan */
  zoom?: boolean;
  /** Sync legend selection */
  legend?: boolean;
}

// Global registry of sync groups
const syncGroups = new Map<string, SyncGroup>();

/**
 * Create or get a sync group
 */
function getOrCreateGroup(
  groupId: string,
  options: SyncGroupOptions = {},
): SyncGroup {
  let group = syncGroups.get(groupId);
  if (!group) {
    group = {
      id: groupId,
      charts: new Set(),
      options: {
        tooltip: true,
        crosshair: true,
        zoom: true,
        legend: true,
        ...options,
      },
    };
    syncGroups.set(groupId, group);
  }
  return group;
}

/**
 * Register a chart with a sync group
 */
function registerChart(groupId: string, chart: ECharts): () => void {
  const group = getOrCreateGroup(groupId);
  group.charts.add(chart);

  // Setup event handlers
  const handlers = setupSyncHandlers(chart, group);

  // Return cleanup function
  return () => {
    handlers.forEach((cleanup) => cleanup());
    group.charts.delete(chart);
    if (group.charts.size === 0) {
      syncGroups.delete(groupId);
    }
  };
}

/**
 * Setup synchronization event handlers
 */
function setupSyncHandlers(
  sourceChart: ECharts,
  group: SyncGroup,
): (() => void)[] {
  const cleanups: (() => void)[] = [];

  // Tooltip/Crosshair sync
  if (group.options.tooltip || group.options.crosshair) {
    const handleAxisPointer = (params: unknown) => {
      const p = params as {
        axesInfo?: { axisDim: string; value: unknown }[];
        seriesIndex?: number;
        dataIndex?: number;
      };

      group.charts.forEach((chart) => {
        if (chart === sourceChart) return;

        try {
          if (p.axesInfo && p.axesInfo.length > 0) {
            const axisInfo = p.axesInfo[0];
            chart.dispatchAction({
              type: "showTip",
              x: axisInfo.value as number,
              seriesIndex: 0,
            });
          } else if (p.dataIndex !== undefined) {
            chart.dispatchAction({
              type: "showTip",
              seriesIndex: p.seriesIndex || 0,
              dataIndex: p.dataIndex,
            });
          }
        } catch (e) {
          // Ignore errors from disposed charts
        }
      });
    };

    const handleMouseOut = () => {
      group.charts.forEach((chart) => {
        if (chart === sourceChart) return;
        try {
          chart.dispatchAction({ type: "hideTip" });
        } catch (e) {
          // Ignore
        }
      });
    };

    sourceChart.on("updateAxisPointer", handleAxisPointer);
    sourceChart.getZr().on("mouseout", handleMouseOut);

    cleanups.push(() => {
      sourceChart.off("updateAxisPointer", handleAxisPointer);
      sourceChart.getZr()?.off("mouseout", handleMouseOut);
    });
  }

  // Zoom sync
  if (group.options.zoom) {
    const handleDataZoom = (params: unknown) => {
      const p = params as {
        batch?: { start: number; end: number }[];
        start?: number;
        end?: number;
      };

      const start = p.batch?.[0]?.start ?? p.start;
      const end = p.batch?.[0]?.end ?? p.end;

      if (start === undefined || end === undefined) return;

      group.charts.forEach((chart) => {
        if (chart === sourceChart) return;
        try {
          chart.dispatchAction({
            type: "dataZoom",
            start,
            end,
          });
        } catch (e) {
          // Ignore
        }
      });
    };

    sourceChart.on("datazoom", handleDataZoom);
    cleanups.push(() => sourceChart.off("datazoom", handleDataZoom));
  }

  // Legend sync
  if (group.options.legend) {
    const handleLegendSelect = (params: unknown) => {
      const p = params as { name: string; selected: Record<string, boolean> };

      group.charts.forEach((chart) => {
        if (chart === sourceChart) return;
        try {
          chart.dispatchAction({
            type: "legendToggleSelect",
            name: p.name,
          });
        } catch (e) {
          // Ignore
        }
      });
    };

    sourceChart.on("legendselectchanged", handleLegendSelect);
    cleanups.push(() =>
      sourceChart.off("legendselectchanged", handleLegendSelect),
    );
  }

  return cleanups;
}

// =============================================================================
// React Hook
// =============================================================================

export interface UseChartSyncOptions extends SyncGroupOptions {
  /** Sync group ID */
  groupId: string;
  /** Whether sync is enabled */
  enabled?: boolean;
}

/**
 * Hook to synchronize a chart with other charts in the same group
 *
 * @example
 * ```tsx
 * const chartRef = useRef<AstroChartHandle>(null);
 * useChartSync(chartRef, { groupId: 'dashboard-charts' });
 * ```
 */
export function useChartSync(
  chartRef: React.RefObject<AstroChartHandle>,
  options: UseChartSyncOptions,
): void {
  const { groupId, enabled = true, ..._syncOptions } = options;
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Get chart instance
    const chart = chartRef.current?.getChart() as ECharts | undefined;
    if (!chart) return;

    // Register with group
    cleanupRef.current = registerChart(groupId, chart);

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [chartRef, groupId, enabled]);
}

// =============================================================================
// Programmatic API
// =============================================================================

/**
 * Dispatch action to all charts in a group
 */
export function dispatchToGroup(
  groupId: string,
  action: Parameters<ECharts["dispatchAction"]>[0],
): void {
  const group = syncGroups.get(groupId);
  if (!group) return;

  group.charts.forEach((chart) => {
    try {
      chart.dispatchAction(action);
    } catch (e) {
      // Ignore disposed charts
    }
  });
}

/**
 * Sync zoom range across all charts in a group
 */
export function syncZoomRange(
  groupId: string,
  start: number,
  end: number,
): void {
  dispatchToGroup(groupId, { type: "dataZoom", start, end });
}

/**
 * Reset zoom for all charts in a group
 */
export function resetGroupZoom(groupId: string): void {
  dispatchToGroup(groupId, { type: "dataZoom", start: 0, end: 100 });
}

/**
 * Show tooltip at specific data index for all charts
 */
export function showGroupTooltip(
  groupId: string,
  seriesIndex: number,
  dataIndex: number,
): void {
  dispatchToGroup(groupId, { type: "showTip", seriesIndex, dataIndex });
}

/**
 * Hide tooltip for all charts in a group
 */
export function hideGroupTooltip(groupId: string): void {
  dispatchToGroup(groupId, { type: "hideTip" });
}

/**
 * Get all chart instances in a group
 */
export function getGroupCharts(groupId: string): ECharts[] {
  const group = syncGroups.get(groupId);
  return group ? Array.from(group.charts) : [];
}

// =============================================================================
// Export Utilities
// =============================================================================

/**
 * Export all charts in a group as images
 */
export function exportGroupAsImages(
  groupId: string,
  options: {
    type?: "png" | "svg";
    pixelRatio?: number;
    fileName?: string;
  } = {},
): string[] {
  const {
    type = "png",
    pixelRatio = 2,
    fileName: _fileName = "chart",
  } = options;
  const charts = getGroupCharts(groupId);

  return charts.map((chart, _index) => {
    return chart.getDataURL({
      type,
      pixelRatio,
      backgroundColor: "transparent",
    });
  });
}

/**
 * Export all charts in a group as a combined CSV
 */
export function exportGroupAsCSV(
  groupId: string,
  options: {
    fileName?: string;
    includeHeaders?: boolean;
  } = {},
): string {
  const { includeHeaders = true } = options;
  const charts = getGroupCharts(groupId);
  const rows: string[][] = [];

  charts.forEach((chart, chartIndex) => {
    const option = chart.getOption();
    const series = option.series as Array<{ name: string; data: unknown[] }>;

    if (!series || series.length === 0) return;

    // Add chart header
    if (includeHeaders) {
      rows.push([`Chart ${chartIndex + 1}`]);
      rows.push([
        "Index",
        ...series.map((s) => s.name || `Series ${chartIndex}`),
      ]);
    }

    // Find max data length
    const maxLen = Math.max(...series.map((s) => s.data?.length || 0));

    // Add data rows
    for (let i = 0; i < maxLen; i++) {
      const row = [i.toString()];
      series.forEach((s) => {
        const value = s.data?.[i];
        if (typeof value === "number") {
          row.push(value.toString());
        } else if (Array.isArray(value)) {
          row.push(value[1]?.toString() || "");
        } else if (value && typeof value === "object") {
          const v = value as { value?: number; y?: number };
          row.push((v.value ?? v.y)?.toString() || "");
        } else {
          row.push("");
        }
      });
      rows.push(row);
    }

    rows.push([]); // Empty row between charts
  });

  return rows.map((row) => row.join(",")).join("\n");
}
